import React from "react";
import * as ReactDOM from "react-dom/client";
import "./landing-shared.entry.jsx";
const SiteNav = globalThis.SiteNav ?? globalThis.window?.SiteNav;
const SiteFooter = globalThis.SiteFooter ?? globalThis.window?.SiteFooter;
// Private Network deep-dive
const { useState, useEffect } = React;

const Icon = {
  arrow: (p) => <svg width="12" height="12" viewBox="0 0 12 12" fill="none" {...p}><path d="M3 6h6m0 0L6 3m3 3L6 9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="square"/></svg>,
};

function Hero() {
  return (
    <section className="section section-dark pa-hero">
      <div className="dark-grid"></div>
      <div className="dark-glow"></div>
      <div className="container">
        <div className="reveal">
          <span className="pa-eyebrow"><span className="eb-dot"></span>PRIVATE NETWORK · <strong>Beta · WireGuard mesh</strong></span>
          <h1>Your devices,<br/>one private network,<br/><em>no gateway in the middle</em>.</h1>
          <p className="pa-hero-sub">
            A WireGuard-based private network between the devices you enroll with named.network — laptop, server, workstation, homelab box. Direct peer-to-peer when possible, with relay-aware diagnostics and route controls from the same workspace.
          </p>
          <div className="pa-hero-ctas">
            <a href="https://app.named.network/signup" className="btn btn-accent btn-lg"><span>Connect my devices <Icon.arrow /></span></a>
            <a href="#how" className="btn btn-on-dark btn-lg"><span>See the topology</span></a>
          </div>
          <div className="pa-hero-trust">
            <span><span className="check">✓</span>WireGuard-based encrypted transport</span>
            <span><span className="check">✓</span>Direct P2P when possible</span>
            <span><span className="check">✓</span>NAT &amp; CGNAT punch-through</span>
            <span><span className="check">✓</span>No central gateway bottleneck</span>
          </div>
        </div>

        <div className="pa-stage reveal" style={{ animationDelay: '160ms' }}>
          <span className="pa-stage-label">LIVE MESH · workspace acme-co · 7 peers</span>
          <span className="pa-stage-corner tl"></span><span className="pa-stage-corner tr"></span>
          <span className="pa-stage-corner bl"></span><span className="pa-stage-corner br"></span>
          <BigStageViz />
        </div>

        <div className="pa-stats">
          {[
            { num: '7', label: 'Connected peers', desc: 'Mix of laptops, servers, and home boxes' },
            { num: '94%', label: 'Direct connections', desc: 'P2P when NAT allows it, relayed when not' },
            { num: '4.2ms', label: 'Median latency', desc: 'Same continent, peer-to-peer' },
            { num: '100%', label: 'Encrypted', desc: 'WireGuard end-to-end, always' },
          ].map((s, i) => (
            <div key={i} className="pa-stat reveal" style={{ animationDelay: `${i*80}ms` }}>
              <div className="num"><em>{s.num.match(/[\d.,]+/)?.[0] || s.num}</em>{s.num.replace(/[\d.,]+/, '')}</div>
              <div className="lbl">{s.label}</div>
              <div className="desc">{s.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BigStageViz() {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => (t + 1) % 30), 100);
    return () => clearInterval(id);
  }, []);
  // Mesh of 7 peers around a circle
  const peers = [
    { angle: -90, name: 'macbook-pro', ip: '100.64.0.2', loc: 'home', online: true },
    { angle: -38, name: 'homelab-01', ip: '100.64.0.3', loc: 'home', online: true },
    { angle: 14, name: 'iphone-priya', ip: '100.64.0.4', loc: 'mobile', online: true },
    { angle: 66, name: 'aws-bastion', ip: '100.64.0.5', loc: 'aws-us', online: true },
    { angle: 118, name: 'workstation', ip: '100.64.0.6', loc: 'office', online: true },
    { angle: 170, name: 'old-server', ip: '100.64.0.7', loc: 'colo', online: false },
    { angle: 222, name: 'rasp-pi', ip: '100.64.0.8', loc: 'home', online: true },
  ];
  const cx = 600, cy = 230, r = 170;
  const pts = peers.map(p => ({
    ...p,
    x: cx + Math.cos(p.angle * Math.PI / 180) * r,
    y: cy + Math.sin(p.angle * Math.PI / 180) * r,
  }));

  // Edges between every pair (mesh)
  const edges = [];
  for (let i = 0; i < pts.length; i++) {
    for (let j = i + 1; j < pts.length; j++) {
      if (pts[i].online && pts[j].online) {
        // a couple are relayed (longer dashed)
        const relayed = (i === 1 && j === 4) || (i === 2 && j === 6);
        edges.push({ a: pts[i], b: pts[j], relayed });
      }
    }
  }

  return (
    <svg viewBox="0 0 1200 460" style={{ width: '100%', height: 'auto', display: 'block', position: 'relative', zIndex: 1 }}>
      {Array.from({length: 9}).map((_, r) =>
        Array.from({length: 18}).map((_, c) =>
          <circle key={`${r}-${c}`} cx={40 + c * 65} cy={40 + r * 50} r="0.7" fill="rgba(255,255,255,0.08)" />
        )
      )}

      {/* Outer perimeter */}
      <circle cx={cx} cy={cy} r={r + 50} fill="none" stroke="var(--accent)" strokeWidth="0.5" strokeDasharray="3 6" opacity="0.35"/>
      <text x={cx} y={cy - r - 60} fontFamily="var(--font-mono)" fontSize="10" fill="var(--accent)" textAnchor="middle" letterSpacing="2">YOUR PRIVATE PERIMETER · 100.64.0.0/24</text>

      {/* Mesh edges */}
      {edges.map((e, i) => (
        <line key={i} x1={e.a.x} y1={e.a.y} x2={e.b.x} y2={e.b.y}
          stroke={e.relayed ? 'var(--text-dark-faint)' : 'var(--accent)'}
          strokeWidth={e.relayed ? '0.5' : '0.7'}
          strokeDasharray={e.relayed ? '3 4' : 'none'}
          opacity={e.relayed ? 0.5 : 0.35}
        />
      ))}

      {/* Animated packet on a couple of edges */}
      {[
        { a: pts[0], b: pts[3], offset: 0 },
        { a: pts[1], b: pts[6], offset: 10 },
        { a: pts[2], b: pts[4], offset: 20 },
      ].map((p, i) => {
        const t = ((tick + p.offset) % 30) / 30;
        const x = p.a.x + (p.b.x - p.a.x) * t;
        const y = p.a.y + (p.b.y - p.a.y) * t;
        return <circle key={i} cx={x} cy={y} r="3" fill="var(--accent)" />;
      })}

      {/* Peers */}
      {pts.map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r="22" fill="var(--bg-dark-card)" stroke={p.online ? 'var(--accent)' : 'var(--text-dark-faint)'} strokeWidth="1.5"/>
          {p.online && <circle cx={p.x} cy={p.y} r="22" fill="var(--accent-fade)"/>}
          <circle cx={p.x} cy={p.y} r={p.online ? '4' : '3'} fill={p.online ? 'var(--accent)' : 'var(--text-dark-faint)'}/>
          <rect x={p.x - 70} y={p.y + 28} width="140" height="34" fill="var(--bg-dark-card)" stroke="var(--border-dark)" strokeWidth="0.5" rx="2"/>
          <text x={p.x} y={p.y + 42} fontFamily="var(--font-mono)" fontSize="9" fill={p.online ? 'var(--text-dark-primary)' : 'var(--text-dark-muted)'} textAnchor="middle">{p.name}</text>
          <text x={p.x} y={p.y + 55} fontFamily="var(--font-mono)" fontSize="8" fill="var(--text-dark-muted)" textAnchor="middle" letterSpacing="0.5">{p.ip} · {p.loc}</text>
        </g>
      ))}

      {/* Center label */}
      <g transform={`translate(${cx}, ${cy})`}>
        <text x="0" y="-5" fontFamily="var(--font-mono)" fontSize="10" fill="var(--text-dark-muted)" textAnchor="middle" letterSpacing="2">P2P MESH</text>
        <text x="0" y="9" fontFamily="var(--font-mono)" fontSize="8" fill="var(--text-dark-faint)" textAnchor="middle" letterSpacing="1">no gateway · no bottleneck</text>
      </g>
    </svg>
  );
}

function Workflow() {
  return (
    <section className="section section-dark" id="how">
      <div className="container">
        <div className="section-h">
          <span className="section-label">From one device to a mesh</span>
          <h2 style={{ color: 'var(--text-dark-primary)' }}>Four steps. Same agent. Mesh forms itself.</h2>
          <p>If you already have devices in named.network, you can flip them into the mesh from the Network page. New devices join with the same install command you'd use for Protected Apps.</p>
        </div>
        <div className="workflow">
          {[
            { num: '01', dur: '~1m', title: 'Install the agent', body: 'Same one-line install. The agent already has WireGuard built in — no separate package.' },
            { num: '02', dur: '~30s', title: 'Enable mesh on the device', body: 'One toggle on the device page. The agent gets its 100.64.x.x address and announces itself.' },
            { num: '03', dur: 'auto', title: 'Peers discover each other', body: 'Hole-punching where possible, encrypted relay where not. You don\u2019t configure routes by hand.' },
            { num: '04', dur: 'now', title: 'Reach the enrolled peers', body: 'Inspect device and route state from the Network page, then connect using the private peer details the workspace exposes.' },
          ].map((s, i) => (
            <div key={i} className="wf-step">
              <div className="wf-num"><span>STEP {s.num}</span><span className="dur">{s.dur}</span></div>
              <h4>{s.title}</h4>
              <p>{s.body}</p>
            </div>
          ))}
        </div>
        <div style={{ textAlign: 'center', marginTop: 48 }}>
          <a href="https://app.named.network/signup" className="btn btn-accent btn-lg"><span>Start a mesh <Icon.arrow /></span></a>
        </div>
      </div>
    </section>
  );
}

function Compare() {
  const rows = [
    { feat: 'Architecture', a: 'Hub & spoke', b: 'Mesh (full)', c: 'Mesh (full)', us: 'Mesh (full)' },
    { feat: 'Direct P2P', a: 'No', b: 'Yes', c: 'Yes', us: 'Yes' },
    { feat: 'NAT/CGNAT punch-through', a: 'N/A', b: 'Yes', c: 'Yes', us: 'Yes' },
    { feat: 'Self-hostable', a: 'Yes', b: 'Partial (control plane closed)', c: 'Yes', us: 'Hosted control plane today' },
    { feat: 'Built-in DNS protection', a: 'No', b: 'No', c: 'No', us: 'Yes — same workspace' },
    { feat: 'Built-in app sharing', a: 'No', b: 'No', c: 'No', us: 'Yes — Protected Apps' },
    { feat: 'Operator history and diagnostics', a: 'Manual', b: 'Limited', c: 'Limited', us: 'Yes — route and health visibility' },
    { feat: 'Personal / beta access', a: 'Yes (self-host)', b: 'Yes (3 users)', c: 'Yes', us: 'Advanced beta' },
  ];
  return (
    <section className="section section-light">
      <div className="container">
        <div className="section-h">
          <span className="section-label on-light">Honest comparison</span>
          <h2>How named.network's mesh stacks up.</h2>
          <p>Tailscale is great. Headscale is great. What named.network adds is the surrounding product surface: Protected Apps, Secure DNS, and operator visibility in the same workspace.</p>
        </div>
        <div className="compare">
          <div className="compare-row head">
            <div>Feature</div>
            <div>Classic VPN</div>
            <div>Tailscale</div>
            <div>Headscale</div>
            <div className="us">named.network</div>
          </div>
          {rows.map((r, i) => (
            <div key={i} className="compare-row">
              <div>{r.feat}</div>
              <div>{r.a}</div>
              <div>{r.b}</div>
              <div>{r.c}</div>
              <div className="us">{r.us}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Features() {
  return (
    <section className="section section-light-alt">
      <div className="container">
        <div className="section-h">
          <span className="section-label on-light">What it actually gives you</span>
          <h2>The boring parts that make it worth running.</h2>
          <p>The mesh itself is one feature. The rest is how it shows up day-to-day.</p>
        </div>

        <FeatureRow
          flip={false}
          tag="01 · ROUTES"
          title="Subnet routes for the boxes you can't install on."
          body="Got a printer, a NAS, a legacy server you can't put an agent on? Pick any peer that can reach it, advertise the subnet, and the whole mesh routes through that peer. Approve routes from the dashboard — they don't auto-activate."
          bullets={[
            "<strong>Subnet routers</strong> bring whole LANs into the mesh",
            "Route approval is a deliberate dashboard action",
            "Route transfer and ownership review happen from the dashboard",
            "Per-route access rules — not every peer needs every subnet",
          ]}
          viz={<RoutesViz />}
        />

        <FeatureRow
          flip={true}
          tag="02 · PEER NAMES"
          title="Peer identities you can recognize."
          body="The network surface stays tied to device identities, private addresses, and route state so you do not have to memorize every IP. The current beta emphasizes peer visibility and diagnostics rather than a polished end-user name-service product."
          bullets={[
            "<strong>Private peer identities</strong> stay visible across the workspace",
            "Diagnostics stay tied to device names and private addresses",
            "Route and peer views show the current private-network state clearly",
            "The current beta stays focused on connectivity and routing first",
          ]}
          viz={<DNSViz />}
        />

        <FeatureRow
          flip={false}
          tag="03 · ROUTE POLICY"
          title="Route policy and peer scoping from the dashboard."
          body="named.network already lets operators control route ownership, approvals, transfers, and policy filters. The current beta is strongest as an operator-managed network surface rather than a polished zero-trust policy product for large organizations."
          bullets={[
            "Route ownership and approval stay explicit",
            "<strong>Tags + groups</strong> are already part of route-policy controls",
            "Risky route and device-setting changes are confirmed before apply",
            "Recent route and peer history stay visible in the page",
          ]}
          viz={<ACLViz />}
        />

        <FeatureRow
          flip={true}
          tag="04 · HEALTH"
          title="When something breaks, you know first."
          body="The Network page is a real observability surface — direct vs relayed status per peer, latency, last handshake, and health warnings tied to current diagnostics. When a peer falls off the mesh, the page points you toward what to check next."
          bullets={[
            "Per-peer: direct/relayed, RTT, last handshake",
            "<strong>Peer diagnostics</strong> and relay fallback counts in the current network view",
            "Health warnings for stuck peers and route conflicts",
            "Concrete next-step guidance from the current watchlist and warnings",
          ]}
          viz={<HealthViz />}
        />
      </div>
    </section>
  );
}

function FeatureRow({ flip, tag, title, body, bullets, viz }) {
  return (
    <div className={`feature-row ${flip ? 'flip' : ''}`}>
      <div className="fr-copy reveal">
        <span className="section-label on-light">{tag}</span>
        <h3>{title}</h3>
        <p>{body}</p>
        <ul className="fr-bullets">
          {bullets.map((b, i) => <li key={i} dangerouslySetInnerHTML={{ __html: b }} />)}
        </ul>
      </div>
      <div className="reveal" style={{ animationDelay: '120ms' }}>{viz}</div>
    </div>
  );
}

function RoutesViz() {
  return (
    <div className="viz-frame" data-label="ROUTES · workspace acme-co">
      <span className="viz-corner tl"></span><span className="viz-corner tr"></span>
      <span className="viz-corner bl"></span><span className="viz-corner br"></span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {[
          { subnet: '10.0.0.0/24', via: 'homelab-01', state: 'approved', desc: 'Home LAN' },
          { subnet: '172.31.0.0/16', via: 'aws-bastion', state: 'approved', desc: 'AWS VPC' },
          { subnet: '192.168.10.0/24', via: 'workstation', state: 'pending', desc: 'Office LAN' },
          { subnet: '10.42.0.0/16', via: 'old-server', state: 'expired', desc: 'Colo · peer offline' },
        ].map((r, i) => (
          <div key={i} style={{ padding: '14px 16px', background: 'var(--bg-light-card)', border: '1px solid var(--border-light)', borderRadius: 2 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--text-light-primary)' }}>{r.subnet}</span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, padding: '2px 8px', border: `1px solid ${r.state === 'approved' ? 'var(--success)' : r.state === 'pending' ? 'var(--accent)' : 'var(--text-light-muted)'}`, color: r.state === 'approved' ? 'var(--success)' : r.state === 'pending' ? 'var(--accent)' : 'var(--text-light-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{r.state}</span>
            </div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-light-secondary)' }}>via <span style={{ color: 'var(--text-light-primary)' }}>{r.via}</span> · {r.desc}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DNSViz() {
  return (
    <div className="viz-frame" data-label="PEER NAMES · resolving workspace devices" style={{ background: 'var(--bg-dark-alt)' }}>
      <span className="viz-corner tl"></span><span className="viz-corner tr"></span>
      <span className="viz-corner bl"></span><span className="viz-corner br"></span>
      <div style={{ background: 'var(--bg-dark-card)', border: '1px solid var(--border-dark)', borderRadius: 2, padding: 16 }}>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-dark-secondary)', lineHeight: 1.8 }}>
          <div><span style={{ color: 'var(--accent)' }}>$</span> ssh homelab-01</div>
          <div style={{ color: 'var(--text-dark-faint)', fontSize: 10, marginLeft: 16 }}>resolving homelab-01 → 100.64.0.3 · direct · 4ms</div>
          <div style={{ color: 'var(--success)', marginLeft: 16 }}>connected.</div>
          <div style={{ marginTop: 12 }}><span style={{ color: 'var(--accent)' }}>$</span> curl http://workstation:3000/api</div>
          <div style={{ color: 'var(--text-dark-faint)', fontSize: 10, marginLeft: 16 }}>resolving workstation → 100.64.0.6 · direct · 8ms</div>
          <div style={{ color: 'var(--success)', marginLeft: 16 }}>200 OK</div>
          <div style={{ marginTop: 12 }}><span style={{ color: 'var(--accent)' }}>$</span> ping rasp-pi</div>
          <div style={{ color: 'var(--text-dark-faint)', fontSize: 10, marginLeft: 16 }}>resolving rasp-pi → 100.64.0.8 · direct · 11ms</div>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 12 }}>
        <div style={{ padding: '10px 12px', background: 'var(--bg-dark-card)', border: '1px solid var(--border-dark)', borderRadius: 2 }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-dark-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>SEARCH DOMAIN</div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-dark-primary)' }}>private peer names</div>
        </div>
        <div style={{ padding: '10px 12px', background: 'var(--bg-dark-card)', border: '1px solid var(--border-dark)', borderRadius: 2 }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-dark-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>SPLIT DNS</div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-dark-primary)' }}>device and route state in one view</div>
        </div>
      </div>
    </div>
  );
}

function ACLViz() {
  return (
    <div className="viz-frame" data-label="ROUTE POLICY · acme-co">
      <span className="viz-corner tl"></span><span className="viz-corner tr"></span>
      <span className="viz-corner bl"></span><span className="viz-corner br"></span>
      <div style={{ background: 'var(--bg-dark)', border: '1px solid var(--border-dark)', borderRadius: 2, padding: 16, fontFamily: 'var(--font-mono)', fontSize: 11, lineHeight: 1.7, color: 'var(--text-dark-secondary)' }}>
        <div style={{ color: 'var(--text-dark-muted)' }}>// engineering peers can reach internal subnets</div>
        <div><span style={{ color: 'var(--accent)' }}>allow</span> group:eng <span style={{ color: 'var(--accent)' }}>→</span> route:office-lan</div>
        <div style={{ marginTop: 8, color: 'var(--text-dark-muted)' }}>// only ops approves prod routes</div>
        <div><span style={{ color: 'var(--accent)' }}>allow</span> group:ops <span style={{ color: 'var(--accent)' }}>→</span> route:prod-db</div>
        <div style={{ marginTop: 8, color: 'var(--text-dark-muted)' }}>// contractors stay on staging</div>
        <div><span style={{ color: 'var(--accent)' }}>allow</span> group:contractors <span style={{ color: 'var(--accent)' }}>→</span> route:staging-lab</div>
        <div style={{ marginTop: 8 }}><span style={{ color: 'var(--accent)' }}>review</span> <span style={{ color: 'var(--text-dark-muted)' }}>pending route changes before apply</span></div>
      </div>
      <div style={{ marginTop: 12, display: 'flex', gap: 6 }}>
        <button style={{ padding: '8px 14px', fontFamily: 'var(--font-mono)', fontSize: 10, background: 'var(--accent)', color: '#fff', border: 'none', textTransform: 'uppercase', letterSpacing: '0.06em', cursor: 'pointer', borderRadius: 2 }}>Review changes</button>
        <button style={{ padding: '8px 14px', fontFamily: 'var(--font-mono)', fontSize: 10, background: 'transparent', color: 'var(--text-light-secondary)', border: '1px solid var(--border-light)', textTransform: 'uppercase', letterSpacing: '0.06em', cursor: 'pointer', borderRadius: 2 }}>Approve route</button>
      </div>
    </div>
  );
}

function HealthViz() {
  return (
    <div className="viz-frame" data-label="NETWORK HEALTH · live">
      <span className="viz-corner tl"></span><span className="viz-corner tr"></span>
      <span className="viz-corner bl"></span><span className="viz-corner br"></span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {[
          { name: 'macbook-pro', mode: 'direct', rtt: '4ms', hs: '2s ago', state: 'good' },
          { name: 'homelab-01', mode: 'direct', rtt: '6ms', hs: '4s ago', state: 'good' },
          { name: 'iphone-priya', mode: 'relayed', rtt: '38ms', hs: '8s ago', state: 'warn' },
          { name: 'aws-bastion', mode: 'direct', rtt: '12ms', hs: '1s ago', state: 'good' },
          { name: 'workstation', mode: 'direct', rtt: '9ms', hs: '3s ago', state: 'good' },
          { name: 'old-server', mode: '—', rtt: '—', hs: '14m ago', state: 'down' },
          { name: 'rasp-pi', mode: 'direct', rtt: '11ms', hs: '5s ago', state: 'good' },
        ].map((p, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '14px 1.4fr 0.8fr 0.6fr 0.8fr', gap: 10, alignItems: 'center', padding: '10px 12px', background: 'var(--bg-light-card)', border: '1px solid var(--border-light)', borderRadius: 2 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.state === 'good' ? 'var(--success)' : p.state === 'warn' ? 'var(--warning)' : 'var(--text-light-muted)' }}></span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-light-primary)' }}>{p.name}</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: p.mode === 'direct' ? 'var(--success)' : p.mode === 'relayed' ? 'var(--warning)' : 'var(--text-light-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{p.mode}</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-light-secondary)', textAlign: 'right' }}>{p.rtt}</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-light-muted)', textAlign: 'right' }}>{p.hs}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MidCTA() {
  return (
    <section className="section section-light" style={{ padding: 0 }}>
      <div className="container">
        <div className="cta-inline">
          <div className="cta-inline-text">
            <h4>One mesh. Every device. No gateway.</h4>
            <p>Available as an advanced Private Network beta surface inside named.network. Same agent, with route and health controls living in the workspace.</p>
          </div>
          <a href="https://app.named.network/signup" className="btn btn-accent btn-lg"><span>Connect my devices <Icon.arrow /></span></a>
        </div>
      </div>
    </section>
  );
}

function FAQ() {
  const faqs = [
    { q: 'Is this just Tailscale with a different paint job?', a: 'It lives in the same broad family of private networking tools, but named.network is being built as a wider private-access product. The current Private Network beta sits alongside Protected Apps, Secure DNS, and the rest of the workspace instead of standing alone as only a mesh product.' },
    { q: 'Will it punch through my carrier-grade NAT?', a: 'Most of the time, yes. We use STUN, hole-punching, and a UPnP/PCP attempt. When the NAT is genuinely symmetric on both sides, we fall back to encrypted relay. The dashboard tells you which mode each peer is using and why.' },
    { q: "Where does the data go through?", a: 'Direct peer-to-peer when possible — the data does not transit our servers at all. When relayed, traffic flows through the closest relay node still encrypted end-to-end. We see ciphertext, never plaintext.' },
    { q: 'What about devices I cannot install an agent on?', a: 'Use a subnet router. Install the agent on any peer that can reach the device (a Pi, a NAS, a tiny VM), advertise the subnet, approve the route in the dashboard, and the whole mesh can reach it.' },
    { q: 'Can I lock down which peer can talk to which?', a: 'The current beta already gives operators route-policy controls, group/tag filters, route approvals, and device-setting controls from the dashboard. It should be described as an advanced operator surface today, not a fully polished enterprise zero-trust policy product yet.' },
    { q: 'Does this work on iOS?', a: 'Private Network is currently strongest as an advanced device and server workflow. The current shipped surface is centered on enrolled devices, routes, and diagnostics rather than a polished mobile-client story.' },
    { q: 'How is mesh different from your Protected Apps?', a: 'Protected Apps gives <em>other people</em> access to <em>your</em> apps via a public hostname. Private Network gives <em>your devices</em> access to <em>each other</em> over a private mesh. Different problems, complementary features.' },
    { q: 'Is the mesh free?', a: 'The current Private Network surface is an advanced beta capability inside named.network. Packaging for broader team and advanced operator use follows the product plan, and we should not promise features like SSO here.' },
  ];
  const [open, setOpen] = useState(0);
  return (
    <section className="section section-light">
      <div className="container-narrow">
        <div className="section-h">
          <span className="section-label on-light">FAQ</span>
          <h2>The questions we get most.</h2>
        </div>
        <div className="faq-list">
          {faqs.map((f, i) => (
            <div key={i} className={`faq-item ${open === i ? 'open' : ''}`}>
              <button className="faq-q" onClick={() => setOpen(open === i ? -1 : i)}>
                <span>{f.q}</span><span className="plus">+</span>
              </button>
              <div className="faq-a" dangerouslySetInnerHTML={{ __html: f.a }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="section section-dark" style={{ padding: '128px 0' }}>
      <div className="dark-grid"></div>
      <div className="dark-glow"></div>
      <div className="container">
        <div className="cta-final">
          <span className="section-label">Last thing</span>
          <h2>Same address.<br/>Anywhere on earth.</h2>
          <p>WireGuard-based private networking, direct P2P when available, route controls, and live health visibility — without forcing every connection through one central gateway.</p>
          <div className="ctas" style={{ marginTop: 36 }}>
            <a href="https://app.named.network/signup" className="btn btn-accent btn-lg"><span>Create my workspace <Icon.arrow /></span></a>
            <a href="https://app.named.network/login" className="btn btn-on-dark btn-lg"><span>Sign in</span></a>
          </div>
          <div style={{ marginTop: 24, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-dark-faint)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Beta · Advanced workspace network surface
          </div>
        </div>
      </div>
    </section>
  );
}

function App() {
  return (
    <>
      <SiteNav />
      <Hero />
      <Workflow />
      <Compare />
      <Features />
      <MidCTA />
      <FAQ />
      <FinalCTA />
      <SiteFooter />
    </>
  );
}

ReactDOM.createRoot(document.getElementById('root')).render(<App />);

